import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import WorkshopPage from "@/pages/WorkshopPage";
import IlotsPage from "@/pages/IlotsPage";
import MachinesPage from "@/pages/MachinesPage";
import PlanningPage from "@/pages/PlanningPage";
import AdministrationPage from "@/pages/AdministrationPage";
import UsersAdminPage from "@/pages/UsersAdminPage";
import WorkingHoursPage from "@/pages/WorkingHoursPage";
import CurrentActionsPage from "@/pages/CurrentActionsPage";
import OperatorPage from "@/pages/OperatorPage";
import ImportPlanningPage from "@/pages/ImportPlanningPage";

// Layout
import AppLayout from "@/components/layout/AppLayout";

// API Config
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Auth Callback Component - REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        
        try {
          const response = await fetch(`${API}/auth/session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ session_id: sessionId })
          });
          
          if (response.ok) {
            const data = await response.json();
            // Clear hash and navigate to dashboard with user data
            window.history.replaceState(null, '', window.location.pathname);
            navigate('/dashboard', { state: { user: data.user }, replace: true });
          } else {
            navigate('/login', { replace: true });
          }
        } catch (error) {
          console.error('Auth callback error:', error);
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };
    
    processAuth();
  }, [navigate]);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground font-mono text-sm uppercase tracking-wider">Connexion en cours...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, checkAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  
  useEffect(() => {
    // If user data was passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setIsChecking(false);
      return;
    }
    
    const verifyAuth = async () => {
      const isAuthenticated = await checkAuth();
      setIsChecking(false);
      if (!isAuthenticated) {
        navigate('/login', { replace: true });
      }
    };
    
    verifyAuth();
  }, [location.state, checkAuth, navigate]);
  
  if (isChecking || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground font-mono text-sm uppercase tracking-wider">Chargement...</p>
        </div>
      </div>
    );
  }
  
  if (!user && !location.state?.user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Main Router Component
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id BEFORE rendering routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DashboardPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/atelier"
        element={
          <ProtectedRoute>
            <AppLayout>
              <WorkshopPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ilots"
        element={
          <ProtectedRoute>
            <AppLayout>
              <IlotsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/machines"
        element={
          <ProtectedRoute>
            <AppLayout>
              <MachinesPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/planning"
        element={
          <ProtectedRoute>
            <AppLayout>
              <PlanningPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/planning/import"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ImportPlanningPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/administration"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AdministrationPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/administration/users"
        element={
          <ProtectedRoute>
            <AppLayout>
              <UsersAdminPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/working-hours"
        element={
          <ProtectedRoute>
            <AppLayout>
              <WorkingHoursPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/current-actions"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CurrentActionsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/operator"
        element={
          <ProtectedRoute>
            <AppLayout>
              <OperatorPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
